/**
 * Internal team labels, from `staff-<team>` Keycloak realm roles.
 *
 * These exist to shape the dashboard for the people using it — a salesperson has
 * no use for the Codex hand-off controls. They are deliberately **not** a
 * permission: no Keycloak policy references those roles, so the API accepts the
 * same requests from every admin regardless of team. Hiding a control here tidies
 * the UI; it does not protect the endpoint behind it.
 *
 * `null` means the account has no staff-team role — every customer user, and any
 * staff member not yet assigned one.
 */
export type StaffTeam = "development" | "sales" | "solutions";

export const STAFF_TEAM_LABELS: Record<StaffTeam, string> = {
  development: "Development",
  sales: "Sales",
  solutions: "Solutions",
};

/**
 * Whether this account is on the development team, and so should see the tools
 * that only make sense while shipping code: the Codex hand-off, priority, and
 * moving tickets between stages.
 */
export function isDevelopmentTeam(staffTeam: string | null | undefined): boolean {
  return staffTeam === "development";
}

/**
 * Whether this account is on the solutions team, who own customer-facing
 * incident response — so they are the ones the critical-bug alarm is for.
 */
export function isSolutionsTeam(staffTeam: string | null | undefined): boolean {
  return staffTeam === "solutions";
}

export function getStaffTeamLabel(staffTeam: string | null | undefined): string | null {
  if (staffTeam == null) return null;
  return STAFF_TEAM_LABELS[staffTeam as StaffTeam] ?? staffTeam;
}
